import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Wordmark } from "@/components/MoonMark";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { shortHash, timeAgo } from "@/lib/meta";
import { Flame, ArrowLeft, ExternalLink } from "lucide-react";

export default function PublicBurns() {
  const [burns, setBurns] = useState([]);
  const [split, setSplit] = useState(null);
  useEffect(() => {
    api.get("/burns/public").then(({ data }) => setBurns(data)).catch(() => {});
    api.get("/burns/public/split").then(({ data }) => setSplit(data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-ink text-cream grain">
      <header className="sticky top-0 z-40 flex h-16 items-center justify-between border-b border-line glass px-5 lg:px-10">
        <Link to="/"><Wordmark /></Link>
        <Link to="/"><Button variant="outline" className="gap-2 border-line bg-surface hover:bg-raised"><ArrowLeft size={15} /> Home</Button></Link>
      </header>

      <div className="mx-auto max-w-4xl px-5 py-14 lg:px-10">
        <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-pulp/30 bg-pulp/10 px-3 py-1.5 text-xs text-pulp">
          <Flame size={13} /> Public ledger
        </div>
        <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Moon Burns</h1>
        <p className="mt-3 max-w-lg text-dust">
          Every fee, buy and burn shows up here. 50% buy+burn $MOON, 50% buy+burn the user's token once tradable.
          It all feeds the burn.
        </p>

        {split && (
          <div className="mt-8 rounded-2xl border border-line bg-surface p-6" data-testid="public-burn-split">
            <div className="mb-4 flex flex-wrap items-end justify-between gap-2">
              <div>
                <p className="text-[11px] uppercase tracking-wider text-dust">The flywheel · total burned</p>
                <p className="font-mono text-3xl font-bold text-pulp" data-testid="split-total">${split.total_usd.toLocaleString()}</p>
              </div>
              <p className="font-mono text-xs text-dust" data-testid="split-count">{split.burn_count} burns on-ledger</p>
            </div>
            <div className="flex h-3 overflow-hidden rounded-full bg-raised">
              <div className="h-full transition-all" style={{ width: `${split.juice_pct}%`, background: "#10E396" }} data-testid="split-juice-bar" />
              <div className="h-full transition-all" style={{ width: `${split.token_pct}%`, background: "#FFC83B" }} data-testid="split-token-bar" />
            </div>
            <div className="mt-4 grid grid-cols-2 gap-4">
              <div className="rounded-xl border border-line bg-raised/40 p-4">
                <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-dust"><span className="h-2 w-2 rounded-full" style={{ background: "#10E396" }} /> $MOON · {split.juice_pct}%</p>
                <p className="mt-1 font-mono text-xl" style={{ color: "#10E396" }} data-testid="split-juice-usd">${split.juice_usd.toLocaleString()}</p>
              </div>
              <div className="rounded-xl border border-line bg-raised/40 p-4">
                <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-dust"><span className="h-2 w-2 rounded-full" style={{ background: "#FFC83B" }} /> Creator tokens · {split.token_pct}%</p>
                <p className="mt-1 font-mono text-xl" style={{ color: "#FFC83B" }} data-testid="split-token-usd">${split.token_usd.toLocaleString()}</p>
              </div>
            </div>
            {split.top_tokens?.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2" data-testid="split-top-tokens">
                <span className="text-[11px] uppercase tracking-wider text-dust/70">Top burned tokens</span>
                {split.top_tokens.map((t) => (
                  <span key={t.token} className="rounded-full border border-line bg-raised px-2.5 py-0.5 font-mono text-xs text-cream">${t.token} · ${t.amount_usd.toLocaleString()}</span>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="mt-8 overflow-hidden rounded-2xl border border-line bg-surface" data-testid="public-burns-table">
          <div className="grid grid-cols-[1fr_1fr_1fr_1.4fr_0.6fr] gap-2 border-b border-line px-5 py-3 text-[11px] uppercase tracking-wider text-dust">
            <span>Token</span><span>Amount</span><span>Chain</span><span>Tx</span><span className="text-right">When</span>
          </div>
          {burns.map((b, i) => (
            <div key={i} className="grid grid-cols-[1fr_1fr_1fr_1.4fr_0.6fr] items-center gap-2 border-b border-line/60 px-5 py-3 font-mono text-sm transition-colors hover:bg-raised/40 animate-pulp-flash">
              <span className={b.kind === "juice" ? "text-juice" : "text-pulp"}>${b.token}</span>
              <span className="text-cream">${Number(b.amount_usd).toFixed(2)}</span>
              <span className="text-dust">{b.chain}</span>
              <a href={`https://${b.chain === "BNB" ? "bscscan.com" : "solscan.io"}/tx/${b.tx_hash}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-ai hover:underline">
                {shortHash(b.tx_hash)} <ExternalLink size={11} />
              </a>
              <span className="text-right text-xs text-dust">{timeAgo(b.created_at)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
